/**
 * Row builders for the supplier audit workbook. Everything here consumes an
 * AuditResult (see ./types.js) and returns plain row objects whose keys are the
 * final column headers, in the order the columns should appear. Nothing in this
 * module touches React or Firestore so it can be unit tested with fixtures.
 *
 * Each tab is described as a list of [header, getter] pairs. The same list
 * produces both the rows and the header row for an empty tab, so the two can
 * never drift apart.
 */

import { supplierKey } from "./types"

/**
 * Formatting helpers. Dates become JS Date objects so json_to_sheet writes real
 * date cells; missing values become "" so every row keeps the same keys.
 */
const yesNo = (value) => (value ? "Yes" : "No")

const text = (value) => (value === null || value === undefined ? "" : value)

const money = (value) => (typeof value === "number" && Number.isFinite(value) ? value : "")

const dateTime = (millis) => (typeof millis === "number" ? new Date(millis) : "")

const dateOnly = (millis) => {
	if (typeof millis !== "number") return ""
	const date = new Date(millis)
	return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const pad = (value) => String(value).padStart(2, "0")

const timeOfDay = (millis) => {
	if (typeof millis !== "number") return ""
	const date = new Date(millis)
	return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatDateTime = (millis) => {
	if (typeof millis !== "number") return ""
	const date = new Date(millis)
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${timeOfDay(millis)}`
}

/**
 * Invoice columns: a single invoice keeps its typed value, several are joined
 * with "; " as strings.
 */
const invoiceColumn = (invoices, single, many) => (invoices.length === 1 ? single(invoices[0]) : invoices.map(many).join("; "))

const invoiceStatus = (invoices) => invoices.map((invoice) => text(invoice.status)).join("; ")

const invoiceAmount = (invoices) =>
	invoiceColumn(
		invoices,
		(invoice) => money(invoice.amount),
		(invoice) => String(money(invoice.amount))
	)

const invoicePaid = (invoices) =>
	invoiceColumn(
		invoices,
		(invoice) => dateTime(invoice.paidMillis),
		(invoice) => formatDateTime(invoice.paidMillis)
	)

const SUPPLIER_TYPES = { hotel: "Hotel", course: "Course", transfer: "Transfer" }

/**
 * Turn a column list into one row. When `item` is missing every column is "",
 * which keeps the key set identical across rows (json_to_sheet derives the
 * header row from the first row's keys).
 */
const rowFrom = (columns, item, extra, prefix = "") =>
	Object.fromEntries(columns.map(([header, get]) => [prefix + header, item ? get(item, extra) : ""]))

const headersFrom = (columns, prefix = "") => columns.map(([header]) => prefix + header)

/**
 * Bookings tab: the booking block from the template.
 */
const BOOKING_COLUMNS = [
	["Lead name", (b) => text(b.leadName)],
	["Lead email", (b) => text(b.leadEmail)],
	["Lead phone", (b) => text(b.leadPhone)],
	["Travel date", (b) => dateOnly(b.travelMillis)],
	["GHD booking ref", (b) => text(b.reference)],
	["Booking confirmed", (b) => yesNo(b.confirmed)],
	["Agent", (b) => text(b.agentName)],
	["Number of pax", (b) => text(b.pax)],
	["Golfers", (b) => text(b.golfers)],
	["Non-golfers", (b) => text(b.nonGolfers)],
	["Package booked", (b) => text(b.packageSummary)],
	["Package title", (b) => text(b.packageTitle)],
	["Booked location", (b) => text(b.bookedLocation)],
	["Enquiry location", (b) => text(b.enquiryLocation)],
	["Package price", (b) => money(b.customerPrice)],
	["Paid by client", (b) => money(b.paidByClient)],
	["Balance outstanding", (b) => money(b.balanceOutstanding)],
]

/**
 * Bookings tab: the hotel block, filled from the first hotel on the booking.
 */
const HOTEL_BLOCK_COLUMNS = [
	["Hotel name", (h) => text(h.supplier.name)],
	["Hotel region", (h) => text(h.supplier.region)],
	["Hotel country", (h) => text(h.supplier.country)],
	["GHD venue ID", (h) => text(h.supplier.id)],
	["Room types required", (h) => text(h.rooms)],
	["Number of nights", (h) => text(h.nights)],
	["Check-in", (h) => dateOnly(h.checkInMillis)],
	["Check-out", (h) => dateOnly(h.checkOutMillis)],
	["Meal plan", (h) => text(h.boardType)],
	["Meal time", (h) => text(h.mealTimes)],
	["Hotel comments", (h) => text(h.comments)],
	["Hotel supplier confirmed", (h) => yesNo(h.supplierConfirmed)],
	["Hotel reservation status", (h) => text(h.reservationStatus)],
	["Hotel confirmation status", (h) => text(h.confirmation.status)],
	["Hotel supplier reference", (h) => text(h.confirmation.reference)],
	["Hotel cost", (h) => money(h.cost)],
	["Hotel currency", (h) => text(h.currency)],
	["Hotel invoice status", (h) => invoiceStatus(h.invoices)],
	["Hotel resort paid", (h, booking) => yesNo(booking.resortsPaid)],
]

/**
 * Bookings tab: one course block, prefixed "Course N - " when emitted.
 */
const COURSE_BLOCK_COLUMNS = [
	["Name", (r) => text(r.supplier.name)],
	["Region", (r) => text(r.supplier.region)],
	["Country", (r) => text(r.supplier.country)],
	["GHD course ID", (r) => text(r.supplier.id)],
	["Date of play", (r) => dateOnly(r.teeTimeMillis)],
	["Tee time", (r) => timeOfDay(r.teeTimeMillis)],
	["No. players", (r) => text(r.players)],
	["Buggies", (r) => text(r.buggies)],
	["Supplier confirmed", (r) => yesNo(r.supplierConfirmed)],
	["Reservation status", (r) => text(r.reservationStatus)],
	["Confirmation status", (r) => text(r.confirmation.status)],
	["Supplier reference", (r) => text(r.confirmation.reference)],
	["Cost", (r) => money(r.cost)],
	["Currency", (r) => text(r.currency)],
	["Invoice status", (r) => invoiceStatus(r.invoices)],
]

const COURSE_BLOCKS = 7

/**
 * Flight and transfer columns shared by the Bookings and Transport tabs. The
 * getters take the booking so the two tabs can order them differently.
 */
const FLIGHT_COLUMNS = [
	["Flights required", (b) => yesNo(b.flights.required)],
	["Flights booked", (b) => yesNo(b.flights.booked)],
	["Flight reference", (b) => text(b.flights.reference)],
	["Flight provider", (b) => text(b.flights.provider)],
	["Outbound flight number", (b) => text(b.flights.outboundFlightNumber)],
	["Origin airport", (b) => text(b.flights.originAirport)],
	["Arrival airport", (b) => text(b.flights.arrivalAirport)],
	["Flight departure", (b) => dateTime(b.flights.departureMillis)],
	["Flight arrival", (b) => dateTime(b.flights.arrivalMillis)],
	["Return flight number", (b) => text(b.flights.returnFlightNumber)],
	["Return departure", (b) => dateTime(b.flights.returnDepartureMillis)],
	["Lookup arrival time", (b) => text(b.flights.lookupArrivalTime)],
	["Lookup destination", (b) => text(b.flights.lookupDestination)],
	["Flights cost", (b) => money(b.flights.cost)],
]

const TRANSFER_COLUMNS = [
	["Airport transfer required", (b) => yesNo(b.transport.airportTransferRequired)],
	["Golf transfer required", (b) => yesNo(b.transport.golfTransferRequired)],
	["Transfer type", (b) => text(b.transport.transferType)],
	["Transfer provider", (b) => text(b.transport.supplier?.name)],
	["Transfer provider email", (b) => text(b.transport.supplier?.email)],
	["Transfers booked", (b) => yesNo(b.transport.transfersBooked)],
	["Transfer reference", (b) => text(b.transport.transfersReference)],
	["Transfer reservation status", (b) => text(b.transport.reservationStatus)],
	["Transfer confirmation status", (b) => text(b.transport.confirmation.status)],
	["Transfer supplier reference", (b) => text(b.transport.confirmation.reference)],
	["Transfer lookup provider", (b) => text(b.transport.lookupProvider)],
	["Transfer lookup description", (b) => text(b.transport.lookupDescription)],
	["Transfer pickup time", (b) => text(b.transport.lookupPickupTime)],
	["Transfer cost", (b) => money(b.transport.cost)],
	["Transfer notes", (b) => text(b.transport.notes)],
]

const EVENT_COLUMNS = [
	["Event tag", (b) => text(b.eventTag)],
	["Event transfer booked", (b) => yesNo(b.transport.eventTransferBooked)],
	["Event transfer reference", (b) => text(b.transport.eventTransferReference)],
]

const TRANSPORT_BLOCK_COLUMNS = [...FLIGHT_COLUMNS, ...TRANSFER_COLUMNS, ...EVENT_COLUMNS]

const BOOKING_HEADERS = [
	...headersFrom(BOOKING_COLUMNS),
	...headersFrom(HOTEL_BLOCK_COLUMNS),
	"Additional hotels",
	...Array.from({ length: COURSE_BLOCKS }, (_, index) => headersFrom(COURSE_BLOCK_COLUMNS, `Course ${index + 1} - `)).flat(),
	"Additional rounds",
	...headersFrom(TRANSPORT_BLOCK_COLUMNS),
]

const buildBookingRow = (booking) => {
	const [firstHotel] = booking.hotels

	const courseBlocks = Array.from({ length: COURSE_BLOCKS }, (_, index) =>
		rowFrom(COURSE_BLOCK_COLUMNS, booking.rounds[index], booking, `Course ${index + 1} - `)
	)

	return {
		...rowFrom(BOOKING_COLUMNS, booking),
		...rowFrom(HOTEL_BLOCK_COLUMNS, firstHotel, booking),
		"Additional hotels": Math.max(booking.hotels.length - 1, 0),
		...Object.assign({}, ...courseBlocks),
		"Additional rounds": Math.max(booking.rounds.length - COURSE_BLOCKS, 0),
		...rowFrom(TRANSPORT_BLOCK_COLUMNS, booking),
	}
}

/**
 * One row per booking in the order of the template, transposed.
 *
 * @param {import("./types").AuditResult} result
 */
export const buildBookingRows = (result) => result.bookings.map(buildBookingRow)

/**
 * Hotels tab: one row per booking-hotel line.
 */
const HOTEL_COLUMNS = [
	["Hotel name", (h) => text(h.supplier.name)],
	["Hotel email", (h) => text(h.supplier.email)],
	["GHD venue ID", (h) => text(h.supplier.id)],
	["Region", (h) => text(h.supplier.region)],
	["Country", (h) => text(h.supplier.country)],
	["GHD booking ref", (h, b) => text(b.reference)],
	["Lead name", (h, b) => text(b.leadName)],
	["Travel date", (h, b) => dateOnly(b.travelMillis)],
	["Number of pax", (h, b) => text(b.pax)],
	["Golfers", (h, b) => text(b.golfers)],
	["Non-golfers", (h, b) => text(b.nonGolfers)],
	["Check-in", (h) => dateOnly(h.checkInMillis)],
	["Check-out", (h) => dateOnly(h.checkOutMillis)],
	["Nights", (h) => text(h.nights)],
	["Room types", (h) => text(h.rooms)],
	["Meal plan", (h) => text(h.boardType)],
	["Meal time", (h) => text(h.mealTimes)],
	["Comments", (h) => text(h.comments)],
	["Supplier confirmed", (h) => yesNo(h.supplierConfirmed)],
	["Reservation status", (h) => text(h.reservationStatus)],
	["Confirmation status", (h) => text(h.confirmation.status)],
	["Supplier reference", (h) => text(h.confirmation.reference)],
	["Confirmed by", (h) => text(h.confirmation.contact)],
	["Confirmation received", (h) => dateTime(h.confirmation.receivedMillis)],
	["Cost", (h) => money(h.cost)],
	["Currency", (h) => text(h.currency)],
	["Invoice status", (h) => invoiceStatus(h.invoices)],
	["Invoice amount", (h) => invoiceAmount(h.invoices)],
	["Invoice paid", (h) => invoicePaid(h.invoices)],
	["Resort paid", (h, b) => yesNo(b.resortsPaid)],
	["Booking confirmed", (h, b) => yesNo(b.confirmed)],
	["Agent", (h, b) => text(b.agentName)],
]

/**
 * Golf tab: one row per round.
 */
const GOLF_COLUMNS = [
	["Course name", (r) => text(r.supplier.name)],
	["Course email", (r) => text(r.supplier.email)],
	["GHD course ID", (r) => text(r.supplier.id)],
	["Region", (r) => text(r.supplier.region)],
	["Country", (r) => text(r.supplier.country)],
	["GHD booking ref", (r, b) => text(b.reference)],
	["Lead name", (r, b) => text(b.leadName)],
	["Travel date", (r, b) => dateOnly(b.travelMillis)],
	["Date of play", (r) => dateOnly(r.teeTimeMillis)],
	["Tee time", (r) => timeOfDay(r.teeTimeMillis)],
	["No. players", (r) => text(r.players)],
	["Buggies", (r) => text(r.buggies)],
	["Buggies booked", (r) => yesNo(r.buggiesBooked)],
	["Buggies cost", (r) => money(r.buggiesCost)],
	["Comments", (r) => text(r.comments)],
	["Supplier confirmed", (r) => yesNo(r.supplierConfirmed)],
	["Reservation status", (r) => text(r.reservationStatus)],
	["Confirmation status", (r) => text(r.confirmation.status)],
	["Supplier reference", (r) => text(r.confirmation.reference)],
	["Confirmed by", (r) => text(r.confirmation.contact)],
	["Confirmation received", (r) => dateTime(r.confirmation.receivedMillis)],
	["Cost per golfer", (r) => money(r.costPerGolfer)],
	["Cost", (r) => money(r.cost)],
	["Currency", (r) => text(r.currency)],
	["Invoice status", (r) => invoiceStatus(r.invoices)],
	["Invoice amount", (r) => invoiceAmount(r.invoices)],
	["Invoice paid", (r) => invoicePaid(r.invoices)],
	["Booking confirmed", (r, b) => yesNo(b.confirmed)],
	["Agent", (r, b) => text(b.agentName)],
]

/**
 * Transport tab: one row per booking with any transfer or flight.
 */
const TRANSPORT_COLUMNS = [
	["Transfer provider", (b) => text(b.transport.supplier?.name)],
	["Transfer provider email", (b) => text(b.transport.supplier?.email)],
	["GHD booking ref", (b) => text(b.reference)],
	["Lead name", (b) => text(b.leadName)],
	["Travel date", (b) => dateOnly(b.travelMillis)],
	["Number of pax", (b) => text(b.pax)],
	["Transfer type", (b) => text(b.transport.transferType)],
	["Airport transfer required", (b) => yesNo(b.transport.airportTransferRequired)],
	["Golf transfer required", (b) => yesNo(b.transport.golfTransferRequired)],
	["Transfers booked", (b) => yesNo(b.transport.transfersBooked)],
	["Transfer reference", (b) => text(b.transport.transfersReference)],
	["Reservation status", (b) => text(b.transport.reservationStatus)],
	["Confirmation status", (b) => text(b.transport.confirmation.status)],
	["Supplier reference", (b) => text(b.transport.confirmation.reference)],
	["Lookup provider", (b) => text(b.transport.lookupProvider)],
	["Lookup description", (b) => text(b.transport.lookupDescription)],
	["Pickup time", (b) => text(b.transport.lookupPickupTime)],
	["Transfer cost", (b) => money(b.transport.cost)],
	["Transfer notes", (b) => text(b.transport.notes)],
	...FLIGHT_COLUMNS,
	...EVENT_COLUMNS,
	["Booking confirmed", (b) => yesNo(b.confirmed)],
	["Agent", (b) => text(b.agentName)],
]

/**
 * Suppliers tab: one row per supplier summary.
 */
const SUPPLIER_COLUMNS = [
	["Supplier", (s) => text(s.name)],
	["Type", (s) => text(SUPPLIER_TYPES[s.kind])],
	["Email", (s) => text(s.email)],
	["Currency", (s) => text(s.currency)],
	["GHD ID", (s) => text(s.id)],
	["Region", (s) => text(s.region)],
	["Country", (s) => text(s.country)],
	["CMS linked", (s) => yesNo(s.cmsLinked)],
	["Region or country missing", (s) => yesNo(!s.region || !s.country)],
	["Bookings", (s) => text(s.bookingCount)],
	["Items", (s) => text(s.itemCount)],
	["Record missing", (s) => yesNo(s.missing)],
]

/**
 * Sort helpers. Names compare case-insensitively; missing dates sort last.
 */
const compareNames = (a, b) => text(a).localeCompare(text(b), "en", { sensitivity: "base" })

const compareMillis = (a, b) => (typeof a === "number" ? a : Number.MAX_SAFE_INTEGER) - (typeof b === "number" ? b : Number.MAX_SAFE_INTEGER)

const hasTransport = (booking) => {
	const { transport, flights } = booking
	const transferType = text(transport.transferTypeKey)
	return Boolean(transport.supplier) || (transferType !== "" && transferType !== "nothing") || flights.required || flights.booked
}

/**
 * One row per booking-hotel line, sorted by supplier name then check-in.
 *
 * @param {import("./types").AuditResult} result
 */
export const buildHotelRows = (result) =>
	result.bookings
		.flatMap((booking) => booking.hotels.map((hotel) => ({ hotel, booking })))
		.sort((a, b) => compareNames(a.hotel.supplier.name, b.hotel.supplier.name) || compareMillis(a.hotel.checkInMillis, b.hotel.checkInMillis))
		.map(({ hotel, booking }) => rowFrom(HOTEL_COLUMNS, hotel, booking))

/**
 * One row per round, sorted by course name then tee time.
 *
 * @param {import("./types").AuditResult} result
 */
export const buildGolfRows = (result) =>
	result.bookings
		.flatMap((booking) => booking.rounds.map((round) => ({ round, booking })))
		.sort((a, b) => compareNames(a.round.supplier.name, b.round.supplier.name) || compareMillis(a.round.teeTimeMillis, b.round.teeTimeMillis))
		.map(({ round, booking }) => rowFrom(GOLF_COLUMNS, round, booking))

/**
 * One row per booking with a transfer supplier, a transfer type, or flights
 * required or booked. Sorted by transfer provider then travel date.
 *
 * @param {import("./types").AuditResult} result
 */
export const buildTransportRows = (result) =>
	result.bookings
		.filter(hasTransport)
		.sort((a, b) => compareNames(a.transport.supplier?.name, b.transport.supplier?.name) || compareMillis(a.travelMillis, b.travelMillis))
		.map((booking) => rowFrom(TRANSPORT_COLUMNS, booking))

/**
 * One row per supplier appearing in the window.
 *
 * @param {import("./types").AuditResult} result
 */
export const buildSupplierRows = (result) => result.suppliers.map((supplier) => rowFrom(SUPPLIER_COLUMNS, supplier))

const bookingsSheet = (result) => ({ name: "Bookings", rows: buildBookingRows(result), headers: BOOKING_HEADERS })

const hotelsSheet = (result) => ({ name: "Hotels", rows: buildHotelRows(result), headers: headersFrom(HOTEL_COLUMNS) })

const golfSheet = (result) => ({ name: "Golf", rows: buildGolfRows(result), headers: headersFrom(GOLF_COLUMNS) })

const transportSheet = (result) => ({ name: "Transport", rows: buildTransportRows(result), headers: headersFrom(TRANSPORT_COLUMNS) })

const suppliersSheet = (result) => ({ name: "Suppliers", rows: buildSupplierRows(result), headers: headersFrom(SUPPLIER_COLUMNS) })

/**
 * The five tabs of the full workbook, ready for exportSheetsToExcel.
 *
 * @param {import("./types").AuditResult} result
 * @returns {Array<{ name: string, rows: Object[], headers: string[] }>}
 */
export const buildWorkbookSheets = (result) => [bookingsSheet(result), hotelsSheet(result), golfSheet(result), transportSheet(result), suppliersSheet(result)]

/**
 * Keep only the hotel and round lines that belong to the chosen suppliers.
 * Transport and flights are booking-level facts (the transfer reference, the
 * flight reference and times) that the template carries for every booking,
 * so they stay on the row whichever supplier the pack is for. Returns null
 * when the booking has nothing for the chosen suppliers.
 */
const trimBooking = (booking, keys) => {
	const hotels = booking.hotels.filter((hotel) => keys.has(supplierKey(hotel.supplier)))
	const rounds = booking.rounds.filter((round) => keys.has(supplierKey(round.supplier)))
	const usesTransfer = Boolean(booking.transport.supplier) && keys.has(supplierKey(booking.transport.supplier))

	if (hotels.length === 0 && rounds.length === 0 && !usesTransfer) return null

	return { ...booking, hotels, rounds }
}

/**
 * A new AuditResult holding only the bookings that use any of the chosen
 * suppliers (keys from supplierKey), each trimmed to those suppliers' lines.
 * The source result is never mutated.
 *
 * @param {import("./types").AuditResult} result
 * @param {string[]} keys
 */
export const filterBySuppliers = (result, keys) => {
	const chosen = new Set(keys)
	const suppliers = result.suppliers.filter((supplier) => chosen.has(supplierKey(supplier)))

	if (suppliers.length === 0) return { ...result, bookings: [], suppliers: [] }

	const bookings = result.bookings.map((booking) => trimBooking(booking, chosen)).filter(Boolean)

	return { ...result, bookings, suppliers }
}

const SHEET_BY_KIND = { hotel: hotelsSheet, course: golfSheet, transfer: transportSheet }

/**
 * Sheets for one supplier's download: the tab matching the supplier's kind plus
 * a Bookings tab of the trimmed bookings.
 *
 * @param {import("./types").AuditResult} result
 * @param {string} key  supplierKey(supplier)
 * @returns {Array<{ name: string, rows: Object[], headers: string[] }>}
 */
/**
 * Workbook sheets for the chosen suppliers: one type tab per kind present
 * (Hotels, Golf, Transport) plus the Bookings tab of the filtered result.
 *
 * @param {import("./types").AuditResult} result
 * @param {string[]} keys
 */
export const buildSupplierWorkbookSheets = (result, keys) => {
	const filtered = filterBySuppliers(result, keys)
	const kinds = new Set(filtered.suppliers.map((supplier) => supplier.kind))
	const typeSheets = ["hotel", "course", "transfer"].filter((kind) => kinds.has(kind)).map((kind) => SHEET_BY_KIND[kind](filtered))

	return [...typeSheets, bookingsSheet(filtered)]
}
